import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, MapPin, Calendar, Briefcase, Clock, ArrowLeft, Target, MessageSquare, Award, Star, GripVertical, Plus, X } from 'lucide-react';
import { KornFerrySkills } from '../skills/KornFerrySkills';
import { useToast } from '../../context/ToastContext';
import { Modal } from '../ui/Modal';
interface SkillLevel {
  skillId: number;
  level: number;
  status: 'not-started' | 'in-development' | 'proficient' | 'expert';
}
interface AgendaItem {
  id: number;
  text: string;
}
export function EmployeeProfile() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    showToast
  } = useToast();
  const [activeTab, setActiveTab] = useState('info');
  // Mock data - En production, ces données viendraient d'une API
  const employee = {
    id: id || '1',
    name: 'Marie Dupont',
    position: 'Développeuse Frontend',
    department: 'Technologie',
    email: 'marie.dupont@construction-gauthier.com',
    phone: '(514) 555-0123',
    address: '123 Rue Principale, Montréal, QC H2X 1Y5',
    birthday: '15 janvier',
    birthYear: 1991,
    hireDate: '1 mars 2020',
    manager: 'Jean Martin',
    avatar: 'MD',
    status: 'active',
    skills: [1, 6, 7, 22, 30] // Compétences Korn Ferry sélectionnées
  };
  const [skillLevels, setSkillLevels] = useState<SkillLevel[]>([{
    skillId: 1,
    level: 65,
    status: 'proficient'
  }, {
    skillId: 6,
    level: 45,
    status: 'in-development'
  }, {
    skillId: 7,
    level: 80,
    status: 'proficient'
  }, {
    skillId: 22,
    level: 90,
    status: 'expert'
  }, {
    skillId: 30,
    level: 55,
    status: 'proficient'
  }]);
  const [trackedSkills, setTrackedSkills] = useState<number[]>([1, 6, 22]); // Compétences suivies
  // Agenda state
  const [isAgendaModalOpen, setIsAgendaModalOpen] = useState(false);
  const [agenda, setAgenda] = useState<AgendaItem[]>([{
    id: 1,
    text: 'Revue des objectifs Q4'
  }, {
    id: 2,
    text: 'Planification 2024'
  }]);
  const [tempAgenda, setTempAgenda] = useState<AgendaItem[]>([]);
  const [newAgendaItem, setNewAgendaItem] = useState('');
  const [draggedItem, setDraggedItem] = useState<number | null>(null);
  // Données de performance
  const performanceData = {
    objectives: [{
      id: 1,
      title: 'Réduire les incidents de sécurité sur les chantiers',
      progress: 85,
      status: 'on-track'
    }, {
      id: 2,
      title: 'Améliorer le respect des échéanciers de projet',
      progress: 70,
      status: 'on-track'
    }],
    recentFeedback: [{
      id: 1,
      from: 'Jean Martin',
      date: '2023-11-10',
      type: 'positive',
      message: 'Excellente gestion du chantier résidentiel.'
    }, {
      id: 2,
      from: 'Thomas Petit',
      date: '2023-11-05',
      type: 'constructive',
      message: 'Pense à mieux coordonner avec les sous-traitants.'
    }],
    upcomingOneOnOne: {
      date: '2023-12-15',
      time: '14:00'
    }
  };
  const calculateAge = (birthYear: number) => {
    return new Date().getFullYear() - birthYear;
  };
  const handleLevelsChange = (newLevels: SkillLevel[]) => {
    setSkillLevels(newLevels);
    showToast('Niveau de compétence mis à jour', 'success');
  };
  const handleTrackedSkillsChange = (newTracked: number[]) => {
    setTrackedSkills(newTracked);
  };
  // Agenda editing functions
  const handleOpenAgendaModal = () => {
    setTempAgenda([...agenda]);
    setIsAgendaModalOpen(true);
  };
  const handleAddAgendaItem = () => {
    if (newAgendaItem.trim()) {
      const newItem: AgendaItem = {
        id: Date.now(),
        text: newAgendaItem.trim()
      };
      setTempAgenda([...tempAgenda, newItem]);
      setNewAgendaItem('');
      showToast('Élément ajouté', 'success');
    }
  };
  const handleRemoveAgendaItem = (itemId: number) => {
    setTempAgenda(tempAgenda.filter(item => item.id !== itemId));
  };
  const handleDragStart = (itemId: number) => {
    setDraggedItem(itemId);
  };
  const handleDragOver = (e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === targetId) return;
    const draggedIndex = tempAgenda.findIndex(item => item.id === draggedItem);
    const targetIndex = tempAgenda.findIndex(item => item.id === targetId);
    if (draggedIndex === -1 || targetIndex === -1) return;
    const newAgenda = [...tempAgenda];
    const [removed] = newAgenda.splice(draggedIndex, 1);
    newAgenda.splice(targetIndex, 0, removed);
    setTempAgenda(newAgenda);
  };
  const handleDragEnd = () => {
    setDraggedItem(null);
  };
  const handleSaveAgenda = () => {
    setAgenda(tempAgenda);
    setIsAgendaModalOpen(false);
    setTempAgenda([]);
    showToast('Ordre du jour mis à jour', 'success');
  };
  return <div className="pb-20 md:pb-0" data-id="element-422">
      {/* Header avec bouton retour */}
      <div className="mb-6" data-id="element-423">
        <button onClick={() => navigate('/manager/equipe')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4" data-id="element-424">
          <ArrowLeft className="h-5 w-5" data-id="element-425" />
          <span data-id="element-426">Retour à l'équipe</span>
        </button>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-427">
          <div className="flex flex-col md:flex-row md:items-center gap-6" data-id="element-428">
            <div className="h-24 w-24 rounded-full bg-blue-600 flex items-center justify-center text-white text-3xl font-bold flex-shrink-0" data-id="element-429">
              {employee.avatar}
            </div>
            <div className="flex-1" data-id="element-430">
              <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-431">
                {employee.name}
              </h1>
              <p className="text-lg text-gray-600 mb-3" data-id="element-432">{employee.position}</p>
              <div className="flex flex-wrap gap-4 text-sm text-gray-600" data-id="element-433">
                <div className="flex items-center gap-1" data-id="element-434">
                  <Briefcase className="h-4 w-4" data-id="element-435" />
                  <span data-id="element-436">{employee.department}</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-437">
                  <Clock className="h-4 w-4" data-id="element-438" />
                  <span data-id="element-439">Depuis {employee.hireDate}</span>
                </div>
                <div className="flex items-center gap-1" data-id="element-440">
                  <span className={`px-2 py-1 rounded-full text-xs ${employee.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-441">
                    {employee.status === 'active' ? 'Actif' : 'Inactif'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Onglets */}
      <div className="bg-white rounded-lg shadow-sm" data-id="element-442">
        <nav className="flex border-b border-gray-200" data-id="element-443">
          <button onClick={() => setActiveTab('info')} className={`px-6 py-4 font-medium text-sm flex items-center gap-2 transition-colors ${activeTab === 'info' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-444">
            <User className="h-4 w-4" data-id="element-445" />
            Informations
          </button>
          <button onClick={() => setActiveTab('performance')} className={`px-6 py-4 font-medium text-sm flex items-center gap-2 transition-colors ${activeTab === 'performance' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-446">
            <Target className="h-4 w-4" data-id="element-447" />
            Performance et développement
          </button>
        </nav>

        <div className="p-6" data-id="element-448">
          {activeTab === 'info' && <div className="space-y-6" data-id="element-449">
              <div data-id="element-450">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-451">
                  Coordonnées
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-452">
                  <div className="flex items-start gap-3" data-id="element-453">
                    <Mail className="h-5 w-5 text-gray-400 mt-0.5" data-id="element-454" />
                    <div data-id="element-455">
                      <p className="text-sm font-medium text-gray-700" data-id="element-456">
                        Courriel
                      </p>
                      <p className="text-sm text-gray-900" data-id="element-457">{employee.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-458">
                    <Phone className="h-5 w-5 text-gray-400 mt-0.5" data-id="element-459" />
                    <div data-id="element-460">
                      <p className="text-sm font-medium text-gray-700" data-id="element-461">
                        Téléphone
                      </p>
                      <p className="text-sm text-gray-900" data-id="element-462">{employee.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 md:col-span-2" data-id="element-463">
                    <MapPin className="h-5 w-5 text-gray-400 mt-0.5" data-id="element-464" />
                    <div data-id="element-465">
                      <p className="text-sm font-medium text-gray-700" data-id="element-466">
                        Adresse
                      </p>
                      <p className="text-sm text-gray-900" data-id="element-467">
                        {employee.address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6" data-id="element-468">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-469">
                  Informations personnelles
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-470">
                  <div className="flex items-start gap-3" data-id="element-471">
                    <Calendar className="h-5 w-5 text-gray-400 mt-0.5" data-id="element-472" />
                    <div data-id="element-473">
                      <p className="text-sm font-medium text-gray-700" data-id="element-474">
                        Date de naissance
                      </p>
                      <p className="text-sm text-gray-900" data-id="element-475">
                        {employee.birthday} ({calculateAge(employee.birthYear)}{' '}
                        ans)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-476">
                    <User className="h-5 w-5 text-gray-400 mt-0.5" data-id="element-477" />
                    <div data-id="element-478">
                      <p className="text-sm font-medium text-gray-700" data-id="element-479">
                        Gestionnaire
                      </p>
                      <p className="text-sm text-gray-900" data-id="element-480">
                        {employee.manager}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6" data-id="element-481">
                <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-482">
                  Informations d'emploi
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-483">
                  <div className="flex items-start gap-3" data-id="element-484">
                    <Briefcase className="h-5 w-5 text-gray-400 mt-0.5" data-id="element-485" />
                    <div data-id="element-486">
                      <p className="text-sm font-medium text-gray-700" data-id="element-487">Poste</p>
                      <p className="text-sm text-gray-900" data-id="element-488">
                        {employee.position}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3" data-id="element-489">
                    <Clock className="h-5 w-5 text-gray-400 mt-0.5" data-id="element-490" />
                    <div data-id="element-491">
                      <p className="text-sm font-medium text-gray-700" data-id="element-492">
                        Date d'embauche
                      </p>
                      <p className="text-sm text-gray-900" data-id="element-493">
                        {employee.hireDate}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>}

          {activeTab === 'performance' && <div className="space-y-6" data-id="element-494">
              {/* Objectifs */}
              <div data-id="element-495">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-496">
                  <Target className="h-5 w-5 text-blue-600" data-id="element-497" />
                  Objectifs en cours
                </h2>
                <div className="space-y-3" data-id="element-498">
                  {performanceData.objectives.map(objective => <div key={objective.id} className="border border-gray-200 rounded-lg p-4" data-id="element-499">
                      <div className="flex justify-between items-start mb-2" data-id="element-500">
                        <h3 className="font-medium text-gray-900" data-id="element-501">
                          {objective.title}
                        </h3>
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs" data-id="element-502">
                          Sur la bonne voie
                        </span>
                      </div>
                      <div className="mb-2" data-id="element-503">
                        <div className="flex justify-between text-sm mb-1" data-id="element-504">
                          <span className="text-gray-600" data-id="element-505">Progression</span>
                          <span className="font-medium" data-id="element-506">
                            {objective.progress}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-507">
                          <div className="bg-green-600 h-2.5 rounded-full" style={{
                      width: `${objective.progress}%`
                    }} data-id="element-508"></div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Feedback récent */}
              <div className="border-t pt-6" data-id="element-509">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-510">
                  <MessageSquare className="h-5 w-5 text-blue-600" data-id="element-511" />
                  Feedback récent
                </h2>
                <div className="space-y-3" data-id="element-512">
                  {performanceData.recentFeedback.map(feedback => <div key={feedback.id} className={`border-l-4 ${feedback.type === 'positive' ? 'border-green-500' : 'border-amber-500'} bg-gray-50 p-4 rounded-r-lg`} data-id="element-513">
                      <div className="flex justify-between items-start mb-2" data-id="element-514">
                        <span className="font-medium text-gray-900" data-id="element-515">
                          De {feedback.from}
                        </span>
                        <span className="text-xs text-gray-500" data-id="element-516">
                          {feedback.date}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700" data-id="element-517">
                        {feedback.message}
                      </p>
                    </div>)}
                </div>
              </div>

              {/* Prochain 1:1 */}
              <div className="border-t pt-6" data-id="element-518">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-519">
                  <Calendar className="h-5 w-5 text-blue-600" data-id="element-520" />
                  Prochain 1:1
                </h2>
                <div className="border border-gray-200 rounded-lg p-4" data-id="element-521">
                  <div className="flex items-center gap-2 mb-3" data-id="element-522">
                    <Calendar className="h-4 w-4 text-gray-600" data-id="element-523" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-524">
                      {performanceData.upcomingOneOnOne.date} à{' '}
                      {performanceData.upcomingOneOnOne.time}
                    </span>
                  </div>
                  <div data-id="element-525">
                    <p className="text-sm font-medium text-gray-700 mb-2" data-id="element-526">
                      Ordre du jour:
                    </p>
                    <ul className="list-disc pl-5 text-sm text-gray-600 space-y-1 mb-4" data-id="element-527">
                      {agenda.map(item => <li key={item.id} data-id="element-528">{item.text}</li>)}
                    </ul>
                    <button onClick={handleOpenAgendaModal} className="px-4 py-2 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200 transition-colors" data-id="element-529">
                      Modifier l'ordre du jour
                    </button>
                  </div>
                </div>
              </div>

              {/* Compétences Korn Ferry */}
              <div className="border-t pt-6" data-id="element-530">
                <div className="flex items-center justify-between mb-4" data-id="element-531">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2" data-id="element-532">
                    <Star className="h-5 w-5 text-blue-600" data-id="element-533" />
                    Compétences Korn Ferry
                  </h2>
                  <p className="text-sm text-gray-600" data-id="element-534">
                    Cliquez sur l'étoile pour suivre et évaluer une compétence
                  </p>
                </div>
                <KornFerrySkills selectedSkills={employee.skills} skillLevels={skillLevels} trackedSkills={trackedSkills} onLevelsChange={handleLevelsChange} onTrackedSkillsChange={handleTrackedSkillsChange} mode="view" showTracking={true} data-id="element-535" />
              </div>
            </div>}
        </div>
      </div>

      {/* Modal pour modifier l'ordre du jour */}
      <Modal isOpen={isAgendaModalOpen} onClose={() => setIsAgendaModalOpen(false)} title="Modifier l'ordre du jour" size="lg" data-id="element-536">
        <div className="space-y-4" data-id="element-537">
          <p className="text-sm text-gray-600" data-id="element-538">
            Glissez-déposez les éléments pour réorganiser l'ordre du jour. Vous
            pouvez aussi ajouter ou supprimer des éléments.
          </p>

          {/* Liste des éléments de l'agenda */}
          <div className="space-y-2" data-id="element-539">
            {tempAgenda.map(item => <div key={item.id} draggable onDragStart={() => handleDragStart(item.id)} onDragOver={e => handleDragOver(e, item.id)} onDragEnd={handleDragEnd} className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg cursor-move hover:bg-gray-100 transition-colors" data-id="element-540">
                <GripVertical className="h-5 w-5 text-gray-400 flex-shrink-0" data-id="element-541" />
                <span className="flex-1 text-sm text-gray-900" data-id="element-542">
                  {item.text}
                </span>
                <button onClick={() => handleRemoveAgendaItem(item.id)} className="p-1 hover:bg-red-100 rounded transition-colors" title="Supprimer" data-id="element-543">
                  <X className="h-4 w-4 text-red-600" data-id="element-544" />
                </button>
              </div>)}
          </div>

          {/* Ajouter un nouvel élément */}
          <div className="border-t pt-4" data-id="element-545">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-546">
              Ajouter un élément
            </label>
            <div className="flex gap-2" data-id="element-547">
              <input type="text" value={newAgendaItem} onChange={e => setNewAgendaItem(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAddAgendaItem()} placeholder="Ex: Discussion sur les objectifs Q1" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-548" />
              <button onClick={handleAddAgendaItem} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2" data-id="element-549">
                <Plus className="h-4 w-4" data-id="element-550" />
                Ajouter
              </button>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t" data-id="element-551">
            <button onClick={() => setIsAgendaModalOpen(false)} className="w-full sm:w-auto px-6 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-552">
              Annuler
            </button>
            <button onClick={handleSaveAgenda} className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium shadow-sm transition-colors" data-id="element-553">
              Enregistrer les modifications
            </button>
          </div>
        </div>
      </Modal>
    </div>;
}